import {noop} from '@benchling/js-utils';
import produce from 'immer';
import React, {useState, useRef, useCallback} from 'react';
import {NodeProps, useReactFlow} from 'reactflow';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {CustomMappingContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/CustomMappingContext';
import {getFieldInfoFromDataFlowEdgeHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {
  convertRootRowToMapFromStartRow,
  filterToAddableMapFromStartRows,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/mapFromStartUtils';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNodeSectionType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNodeData,
  TaskSchemaNodeRowData,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  isLookupConfiguredForEdge,
  FlowchartNodeChangeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';
import FlowchartNodeToolbar from 'ui-library/reactflow/nodes/toolbar/FlowchartNodeToolbar';

import {NodesContext} from '../NodesContext';
import AddFieldToSectionButton from './AddFieldToSectionButton';
import createHeaderPropsForNode from './createHeaderPropsForNode';
import {getNodeAndRowStatusAndDescription} from './nodeUtils';
import {FlowchartNodeEditMode} from './ProcessFlowchartNodeAdapter';
import {TaskSchemaNodeRowRenderer} from './TaskSchemaNodeRowRenderer';

export default function TaskSchemaNodeRenderer(props: NodeProps<TaskSchemaNodeData>): JSX.Element {
  const {data, xPos, yPos, id} = props;
  // Row and handle hovers do no propagate up. This hovered state primarily indicates hovering on the header
  const [isHovered, setIsHovered] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const reactFlowInstance = useReactFlow();
  const {sections, onNodeHeaderClick, onNodeDelete, onUpdateNodeData} = data;
  const locked = ReactFlowLockedContext.useContext() ?? false;

  const headerProps = createHeaderPropsForNode(
    {
      ...data,
      position: {x: xPos, y: yPos},
    },
    reactFlowInstance,
    data.nodeDetailsType !== ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
    data.nodeDetailsType !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
    locked
  );

  const nodesContext = NodesContext.useContext();
  const customMappingDataByEdgeId = CustomMappingContext.useContext();

  const executionType =
    data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType;
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);
  const isDeletable = adapter.isDeletable();
  const isDoubleClickEditMode = adapter.editMode === FlowchartNodeEditMode.DOUBLE_CLICK;
  const isNodeNameDoubleClickEditable = adapter.isNodeNameDoubleClickEditable && !locked;

  const handleNodeNameComplete = useCallback(
    (newName: string) => {
      if (newName.trim() === '' || newName === data.nodeName) {
        return;
      }
      const updatedSpec = adapter.onUpdateNodeName(data.taskDefinitionVersionSpec, newName);
      if (updatedSpec == null) {
        return;
      }

      onUpdateNodeData((flowchartConfig: FlowchartConfigWithMetadata) => {
        const newNodeData = produce(data, (draftNode) => {
          draftNode.nodeName = newName;
          draftNode.taskDefinitionVersionSpec = updatedSpec;
        });
        return [{id: data.id, data: newNodeData, type: FlowchartNodeChangeType.REPLACE}];
      });
    },
    [adapter, data, onUpdateNodeData]
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const editClickHandler =
    onNodeHeaderClick != null && !isEditingName && !isDoubleClickEditMode ? {onClick: onNodeHeaderClick} : {};

  // Check if lookup is configured for a specific row
  const isLookupConfiguredForRow = (nodeId: string, rowClientId: string): boolean => {
    const edges = reactFlowInstance.getEdges();

    const relevantEdges = edges.filter(
      (edge) =>
        edge.source === nodeId &&
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        edge.sourceHandle &&
        getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId === rowClientId
    );

    return relevantEdges.some((edge) => isLookupConfiguredForEdge(edge.id, customMappingDataByEdgeId));
  };

  const {nodeStatus, nodeDescription, rowStatusAndDescriptionById} = getNodeAndRowStatusAndDescription(
    data.validationErrors
  );

  const mapFromStartSection = data.sections.find(
    (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
  );
  let addFieldToMapFromStartSectionButton: JSX.Element | null;
  if (mapFromStartSection) {
    const rootRows = nodesContext.startNode?.data.sections.flatMap((section) => section.sectionRows) ?? [];
    const addableRows = filterToAddableMapFromStartRows(rootRows, mapFromStartSection.sectionRows);
    addFieldToMapFromStartSectionButton = (
      <AddFieldToSectionButton
        rows={addableRows}
        icon="plus"
        disabled={locked || addableRows.length === 0}
        onAddFieldToSection={(rowData: TaskSchemaNodeRowData) => {
          onUpdateNodeData(
            (flowchartConfig: FlowchartConfigWithMetadata) => {
              const newNodeData = produce(data, (draftNode) => {
                draftNode.sections = draftNode.sections.map((section) => {
                  if (section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
                    section.sectionRows.push(convertRootRowToMapFromStartRow(rowData));
                    return section;
                  }
                  return section;
                });
              });
              return [{id: data.id, data: newNodeData, type: FlowchartNodeChangeType.REPLACE}];
            },
            false,
            true // Skip setting dirty state to prevent autosave when adding map from start field
          );
        }}
      />
    );
  } else {
    addFieldToMapFromStartSectionButton = null;
  }

  const isDisabled = data.isDisabled || data.isArchived || false;
  const disabledTooltip = data.isArchived ? 'Remove or replace this archived task schema' : undefined;

  return (
    <div ref={containerRef}>
      <FlowchartNodeToolbar
        isLocked={locked || isDisabled}
        onNodeEdit={isDoubleClickEditMode ? onNodeHeaderClick : undefined}
        onNodeDelete={isDeletable ? onNodeDelete : undefined}
      />
      <BenchlingNodeV2
        isSelected={props.selected}
        isDisabled={isDisabled}
        status={nodeStatus}
        statusDescription={nodeDescription}
        onMouseOver={() => {
          setIsHovered(true);
        }}
        onMouseOut={() => {
          setIsHovered(false);
        }}
        dataTestAttrs={{
          element: 'TaskSchemaNodeRenderer',
          key: headerProps.title,
        }}
      >
        {headerProps && (
          <BenchlingNodeV2.Standard.Header
            {...headerProps}
            {...editClickHandler}
            disabledTooltip={disabledTooltip}
            style={{
              cursor: onNodeHeaderClick != null ? 'pointer' : 'default',
            }}
            doubleClickEditableTitleProps={
              isNodeNameDoubleClickEditable
                ? {
                    isEditing: isEditingName,
                    setIsEditing: setIsEditingName,
                    onComplete: handleNodeNameComplete,
                    onDraftNameChanged: noop,
                    defaultValue: () => data.nodeName,
                  }
                : undefined
            }
          />
        )}
        {sections && sections.length > 0 && (
          <BenchlingNodeV2.Sections>
            {sections.map((section: TaskSchemaNodeSectionData) => (
              <BenchlingNodeV2.Section
                key={section.sectionName}
                leftHeader={section.sectionName}
                rightHeader={
                  section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
                    ? addFieldToMapFromStartSectionButton
                    : undefined
                }
              >
                {section.sectionRows.map((row) => (
                  <TaskSchemaNodeRowRenderer
                    key={row.id}
                    {...row}
                    status={rowStatusAndDescriptionById[row.clientId]?.status ?? undefined}
                    statusDescription={rowStatusAndDescriptionById[row.clientId]?.description ?? undefined}
                    secondaryIcon={isLookupConfiguredForRow(id, row.clientId) ? 'entity-lookup' : undefined}
                    isHovered={isHovered}
                  />
                ))}
              </BenchlingNodeV2.Section>
            ))}
          </BenchlingNodeV2.Sections>
        )}
      </BenchlingNodeV2>
    </div>
  );
}
